import { hashKey } from '@tanstack/react-query';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { type FormatOptionLabelMeta, type GroupBase } from 'react-select';
import { type AsyncProps as ReactAsyncSelectProps } from 'react-select/async';

import {
  type AsyncCreatableSelectProps,
  type AsyncOption,
  type AsyncResolvedOption,
  type AsyncSelectProps,
} from './types';
import { useAsyncSelectControl } from './useAsyncSelectControl';

const EMPTY_ERRORS: readonly string[] = [];
const DEFAULT_DEBOUNCE_TIMEOUT = 300;

function shouldLoadMore(
  scrollHeight: number,
  clientHeight: number,
  scrollTop: number,
) {
  return ((scrollHeight - clientHeight) * 2) / 3 < scrollTop;
}

function getEmptyOption(
  emptyOption: { readonly label?: string; readonly value: string } | undefined,
  defaultLabel: string,
) {
  return emptyOption
    ? {
        label: emptyOption.label ?? defaultLabel,
        value: emptyOption.value,
      }
    : undefined;
}

function getOptionFormatter<TItem>(
  formatter:
    | ((
        option: AsyncResolvedOption<TItem>,
        meta: FormatOptionLabelMeta<AsyncOption<TItem>>,
      ) => ReactNode)
    | undefined,
) {
  return (
    option: AsyncOption<TItem>,
    meta: FormatOptionLabelMeta<AsyncOption<TItem>>,
  ) =>
    option.status === 'resolved' && formatter
      ? formatter(option, meta)
      : option.label;
}

type AsyncSelectInputProps<TItem, TPageParam, TFilters> =
  | AsyncSelectProps<TItem, TPageParam, TFilters>
  | AsyncCreatableSelectProps<TItem, TPageParam, TFilters>;

export function useAsyncSelectProps<TItem, TPageParam, TFilters>(
  props: AsyncSelectInputProps<TItem, TPageParam, TFilters>,
  { isCreatable }: { isCreatable: boolean },
) {
  const {
    'aria-describedby': ariaDescribedBy,
    dataSource,
    debounceTimeout = DEFAULT_DEBOUNCE_TIMEOUT,
    emptyOption,
    errors = EMPTY_ERRORS,
    filters,
    formatOptionLabel,
    isMulti = false,
    isRemovableValue,
    onChange,
    onMultiValueClick,
    shouldLoadMore: shouldLoadMoreOverride = shouldLoadMore,
    testId,
    unresolvedLabel,
    value,
    ...selectProps
  } = props;
  const { t } = useTranslation('ui');
  const generatedInputId = useId();
  const inputId = selectProps.inputId ?? generatedInputId;
  const filtersKey = hashKey([filters]);
  const localizedEmptyOption = getEmptyOption(
    emptyOption,
    t(($) => $.selectControl.emptyOption),
  );
  const control = useAsyncSelectControl({
    classNames: selectProps.classNames,
    components: selectProps.components,
    dataSource,
    debounceTimeout,
    emptyOption: localizedEmptyOption,
    errors,
    filters,
    isCreatable,
    isMulti,
    isRemovableValue,
    onChange(nextValue, items) {
      if (isMulti) {
        (onChange as (value: string[], items: readonly TItem[]) => void)(
          nextValue as string[],
          items as readonly TItem[],
        );
      } else {
        (onChange as (value: string | null, item: TItem | undefined) => void)(
          nextValue as string | null,
          items as TItem | undefined,
        );
      }
    },
    onMultiValueClick,
    styles: selectProps.styles,
    unresolvedLabel,
    value,
  });
  const loadErrorId = `${inputId}-load-error`;
  const describedBy = [
    ariaDescribedBy,
    control.hasLoadError ? loadErrorId : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  const loadErrorMessage = control.hasLoadError
    ? t(($) => $.selectControl.error.loadOptions)
    : undefined;

  const commonProps = {
    ...selectProps,
    'aria-describedby': describedBy || undefined,
    classNames: control.classNames,
    components: control.components,
    defaultOptions: selectProps.defaultOptions ?? true,
    formatCreateLabel:
      'formatCreateLabel' in selectProps && selectProps.formatCreateLabel
        ? selectProps.formatCreateLabel
        : (inputValue: string) =>
            t(($) => $.selectControl.createLabel, { inputValue }),
    formatOptionLabel: getOptionFormatter(formatOptionLabel),
    inputId,
    isClearable: selectProps.isClearable ?? true,
    isMulti,
    isSearchable: selectProps.isSearchable ?? true,
    loadingMessage:
      selectProps.loadingMessage ??
      (() => t(($) => $.selectControl.loadingMessage)),
    menuPortalTarget:
      selectProps.menuPortalTarget ??
      (typeof document === 'undefined' ? undefined : document.body),
    noOptionsMessage:
      selectProps.noOptionsMessage ??
      (() => loadErrorMessage ?? t(($) => $.selectControl.noOptionsMessage)),
    onChange: control.handleChange,
    placeholder:
      selectProps.placeholder ?? t(($) => $.selectControl.placeholder),
    styles: control.styles,
    value: control.value,
  };

  return {
    commonProps: commonProps as Omit<
      ReactAsyncSelectProps<
        AsyncOption<TItem>,
        boolean,
        GroupBase<AsyncOption<TItem>>
      >,
      'loadOptions'
    >,
    control,
    debounceTimeout,
    filters,
    filtersKey,
    loadError: loadErrorMessage
      ? { id: loadErrorId, message: loadErrorMessage }
      : undefined,
    shouldLoadMore: shouldLoadMoreOverride,
    testId,
  };
}
